import React, { useState } from 'react'
import { 
  Row, 
  Col, 
  Card, 
  Button, 
  Typography, 
  Space, 
  List, 
  Tag, 
  Modal, 
  Tabs,
  Statistic,
  Alert,
  Empty,
  Tooltip,
  Badge
} from 'antd'
import {
  PlusOutlined,
  EyeOutlined,
  EditOutlined,
  ShareAltOutlined,
  DownloadOutlined,
  HomeOutlined,
  BuildOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  StarOutlined,
  GiftOutlined,
  TrophyOutlined,
  RocketOutlined
} from '@ant-design/icons'
import { env } from '../config/environment'

const { Title, Text, Paragraph } = Typography
const { TabPane } = Tabs

interface VisualizerDesign {
  id: string
  name: string
  type: 'adu' | 'pool' | 'combo'
  status: 'draft' | 'quoted' | 'approved' | 'completed'
  createdAt: string
  updatedAt: string
  thumbnailUrl?: string
  estimatedCost: number
  quotedCost?: number
  description?: string
  features: string[]
}

interface UpsellOpportunity {
  id: string
  title: string
  description: string
  potentialValue: number
  category: 'upgrade' | 'addon' | 'maintenance' | 'seasonal'
  priority: 'high' | 'medium' | 'low'
  relatedDesignId?: string
}

export const VisualizerDashboard: React.FC = () => {
  const [selectedDesign, setSelectedDesign] = useState<VisualizerDesign | null>(null)
  const [showVisualizerModal, setShowVisualizerModal] = useState(false)
  const [activeTab, setActiveTab] = useState('designs')

  // Mock data - replace with actual API calls
  const mockDesigns: VisualizerDesign[] = [
    {
      id: '1',
      name: 'Backyard ADU Design',
      type: 'adu',
      status: 'quoted',
      createdAt: '2024-01-15T10:00:00Z',
      updatedAt: '2024-01-16T14:30:00Z',
      estimatedCost: 85000,
      quotedCost: 92000,
      description: '500 sq ft studio ADU with modern finishes',
      features: ['Studio Layout', 'Full Kitchen', 'Modern Bathroom', 'Energy Efficient']
    },
    {
      id: '2',
      name: 'Swimming Pool Project',
      type: 'pool',
      status: 'draft',
      createdAt: '2024-01-10T09:00:00Z',
      updatedAt: '2024-01-12T16:45:00Z',
      estimatedCost: 45000,
      description: '16x32 rectangular pool with spa',
      features: ['Rectangular Pool', 'Attached Spa', 'LED Lighting', 'Automatic Cover']
    }
  ]

  const mockUpsells: UpsellOpportunity[] = [
    {
      id: '1',
      title: 'Smart Home Integration',
      description: 'Add smart lighting, climate control, and security to your ADU',
      potentialValue: 8500,
      category: 'upgrade',
      priority: 'high',
      relatedDesignId: '1'
    },
    {
      id: '2',
      title: 'Pool Heating System',
      description: 'Extend your swimming season with an efficient heating system',
      potentialValue: 4200,
      category: 'addon',
      priority: 'medium',
      relatedDesignId: '2'
    },
    {
      id: '3',
      title: 'Seasonal Pool Maintenance',
      description: 'Professional maintenance package for year-round pool care',
      potentialValue: 1800,
      category: 'maintenance',
      priority: 'medium'
    }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success'
      case 'approved': return 'processing'
      case 'quoted': return 'warning'
      case 'draft': return 'default'
      default: return 'default'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed': return <CheckCircleOutlined />
      case 'approved': return <RocketOutlined />
      case 'quoted': return <DollarOutlined />
      case 'draft': return <ClockCircleOutlined />
      default: return <ClockCircleOutlined />
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return '#ff4d4f'
      case 'medium': return '#fa8c16'
      case 'low': return '#52c41a'
      default: return '#666'
    }
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'upgrade': return <StarOutlined />
      case 'addon': return <PlusOutlined />
      case 'maintenance': return <BuildOutlined />
      case 'seasonal': return <GiftOutlined />
      default: return <TrophyOutlined />
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const handleCreateNewDesign = () => {
    setShowVisualizerModal(true)
  }

  const handleViewDesign = (design: VisualizerDesign) => {
    setSelectedDesign(design)
    setShowVisualizerModal(true)
  }

  const totalEstimatedValue = mockDesigns.reduce((sum, design) => sum + design.estimatedCost, 0)
  const totalQuotedValue = mockDesigns.reduce((sum, design) => sum + (design.quotedCost || 0), 0)
  const totalUpsellValue = mockUpsells.reduce((sum, upsell) => sum + upsell.potentialValue, 0)

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <Title level={2} style={{ marginBottom: '8px' }}>
          Project Visualizer
        </Title>
        <Text type="secondary" style={{ fontSize: '16px' }}>
          Design, visualize, and manage your construction projects
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '32px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Active Designs"
              value={mockDesigns.length}
              prefix={<HomeOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Estimated Value"
              value={totalEstimatedValue}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<DollarOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Quoted Value"
              value={totalQuotedValue}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#fa8c16' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Upsell Potential"
              value={totalUpsellValue}
              formatter={(value) => formatCurrency(Number(value))}
              prefix={<TrophyOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content Tabs */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="My Designs" key="designs">
            <div style={{ marginBottom: '16px' }}>
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={handleCreateNewDesign}
                size="large"
              >
                Create New Design
              </Button>
            </div>

            {mockDesigns.length > 0 ? (
              <List
                grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3 }}
                dataSource={mockDesigns}
                renderItem={(design) => (
                  <List.Item>
                    <Card
                      hoverable
                      cover={
                        <div style={{ 
                          height: '200px', 
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '48px'
                        }}>
                          {design.type === 'adu' ? <HomeOutlined /> : <BuildOutlined />}
                        </div>
                      }
                      actions={[
                        <Tooltip title="View Design">
                          <Button 
                            type="text" 
                            icon={<EyeOutlined />}
                            onClick={() => handleViewDesign(design)}
                          />
                        </Tooltip>,
                        <Tooltip title="Edit Design">
                          <Button 
                            type="text" 
                            icon={<EditOutlined />}
                            onClick={() => handleViewDesign(design)}
                          />
                        </Tooltip>,
                        <Tooltip title="Share Design">
                          <Button type="text" icon={<ShareAltOutlined />} />
                        </Tooltip>,
                        <Tooltip title="Download Quote">
                          <Button type="text" icon={<DownloadOutlined />} />
                        </Tooltip>
                      ]}
                    >
                      <Card.Meta
                        title={
                          <Space>
                            {design.name}
                            <Tag 
                              color={getStatusColor(design.status)}
                              icon={getStatusIcon(design.status)}
                            >
                              {design.status.toUpperCase()}
                            </Tag>
                          </Space>
                        }
                        description={
                          <div>
                            <Paragraph ellipsis={{ rows: 2 }}>
                              {design.description}
                            </Paragraph>
                            <Space wrap>
                              {design.features.slice(0, 3).map(feature => (
                                <Tag key={feature}>{feature}</Tag>
                              ))}
                              {design.features.length > 3 && (
                                <Tag>+{design.features.length - 3} more</Tag>
                              )}
                            </Space>
                            <div style={{ marginTop: '8px' }}>
                              <Text strong>{formatCurrency(design.quotedCost || design.estimatedCost)}</Text>
                              <Text type="secondary" style={{ marginLeft: '8px' }}>
                                Updated {formatDate(design.updatedAt)}
                              </Text>
                            </div>
                          </div>
                        }
                      />
                    </Card>
                  </List.Item>
                )}
              />
            ) : (
              <Empty
                description="No designs yet"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button type="primary" onClick={handleCreateNewDesign}>
                  Create Your First Design
                </Button>
              </Empty>
            )}
          </TabPane>

          <TabPane tab={<Badge count={mockUpsells.length} size="small">Recommendations</Badge>} key="upsells">
            <Alert
              message="Personalized Recommendations"
              description="Based on your current projects and preferences, here are some upgrade opportunities that could enhance your investment."
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />

            <List
              dataSource={mockUpsells}
              renderItem={(upsell) => (
                <List.Item
                  actions={[
                    <Button type="primary">Get Quote</Button>,
                    <Button>Learn More</Button>
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <div style={{ 
                        width: '48px', 
                        height: '48px', 
                        borderRadius: '50%',
                        background: getPriorityColor(upsell.priority),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '20px'
                      }}>
                        {getCategoryIcon(upsell.category)}
                      </div>
                    }
                    title={
                      <Space>
                        {upsell.title}
                        <Tag color={getPriorityColor(upsell.priority)}>
                          {upsell.priority.toUpperCase()} PRIORITY
                        </Tag>
                        <Tag>{upsell.category.toUpperCase()}</Tag>
                      </Space>
                    }
                    description={
                      <div>
                        <Paragraph>{upsell.description}</Paragraph>
                        <Text strong style={{ color: '#52c41a', fontSize: '16px' }}>
                          Potential Value: {formatCurrency(upsell.potentialValue)}
                        </Text>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </TabPane>

          <TabPane tab="Project History" key="history">
            <Empty
              description="No completed projects yet"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            >
              <Text type="secondary">
                Your completed projects and their outcomes will appear here
              </Text>
            </Empty>
          </TabPane>
        </Tabs>
      </Card>

      {/* Visualizer Modal */}
      <Modal
        title={selectedDesign ? `Edit: ${selectedDesign.name}` : "Create New Design"}
        open={showVisualizerModal}
        onCancel={() => {
          setShowVisualizerModal(false)
          setSelectedDesign(null)
        }}
        width="90vw"
        style={{ top: 20 }}
        footer={null}
        destroyOnClose
      >
        <div style={{ height: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <iframe
            src={selectedDesign ? 
              `${env.visualizerUrl}?designId=${selectedDesign.id}&designType=${selectedDesign.type}&autoLoad=true` : 
              env.visualizerUrl
            }
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              borderRadius: '8px'
            }}
            title="ADU & Pool Visualizer"
          />
        </div>
      </Modal>
    </div>
  )
}
